import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminApps } from '@/admin/useAdminApps';
import { useRoleApps, useSetRoleApps } from '@/admin/useAdminRoles';

export interface RoleAppsPanelProps {
  roleId: number;
}

export function RoleAppsPanel({ roleId }: RoleAppsPanelProps) {
  const { t } = useTranslation();
  const apps = useAdminApps(false);
  const roleApps = useRoleApps(roleId);
  const setApps = useSetRoleApps(roleId);

  const selected = useMemo(
    () => new Set((roleApps.data ?? []).map((a) => a.app_id)),
    [roleApps.data],
  );

  function toggle(appId: number, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(appId);
    else next.delete(appId);
    setApps.mutate([...next]);
  }

  const loading = apps.isLoading || roleApps.isLoading;

  return (
    <div className="space-y-2 rounded-md border bg-white p-3">
      <h4 className="text-sm font-semibold text-appsol-900">{t('roles.apps.title')}</h4>

      {loading && <p className="text-xs text-slate-500">{t('home.loading')}</p>}
      {(apps.isError || roleApps.isError) && (
        <p role="alert" className="text-xs text-rose-700">
          {t('roles.apps.error')}
        </p>
      )}

      {apps.data && apps.data.length === 0 && (
        <p className="text-xs italic text-slate-500">{t('roles.apps.noApps')}</p>
      )}

      {apps.data && apps.data.length > 0 && (
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {apps.data.map((app) => (
            <li key={app.id}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(app.id)}
                  disabled={setApps.isPending}
                  onChange={(e) => toggle(app.id, e.target.checked)}
                />
                <span>{app.name}</span>
                <code className="rounded bg-slate-100 px-1 text-xs text-slate-500">
                  {app.slug}
                </code>
              </label>
            </li>
          ))}
        </ul>
      )}

      {setApps.isError && (
        <p role="alert" className="text-xs text-rose-700">
          {t('roles.apps.saveError')}
        </p>
      )}
    </div>
  );
}
